// @ts-nocheck
// ============================================================
// OBSERVABILITY — Fase 8.12 + Fase 11 (Hardened)
// FIX: buffer race condition, retry on failure, flush lock,
//      pipeline logs in buffer, structured correlation IDs,
//      tiered fallback: console → localStorage → Supabase
// ============================================================
import { supabase } from '@/lib/supabase'

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal'

export interface LogEntry {
  company_id?:    string
  obra_id?:       string
  level:          LogLevel
  category:       string
  message:        string
  context?:       Record<string, unknown>
  duration_ms?:   number
  correlation_id?: string
}

// ─── State ─────────────────────────────────────────────────
const BUFFER:       LogEntry[] = []
const RETRY_QUEUE:  LogEntry[] = []   // FIX PROB-11: requeue on failure
const FLUSH_INTERVAL = 10_000
const FLUSH_LIMIT    = 50
const MAX_BUFFER     = 500            // prevent unbounded growth
const LS_KEY         = 'canteiro:obs_fallback'

let flushTimer:   ReturnType<typeof setInterval> | null = null
let isFlushing    = false             // FIX PROB-11: flush lock
let remoteLoggingDisabled = false

// ─── Flush ─────────────────────────────────────────────────
async function flush(): Promise<void> {
  if (isFlushing) return              // prevent concurrent flushes
  if (remoteLoggingDisabled) return
  if (BUFFER.length === 0 && RETRY_QUEUE.length === 0) return

  isFlushing = true

  // Prioritize retry queue, then new entries
  const fromRetry = RETRY_QUEUE.splice(0, Math.min(RETRY_QUEUE.length, 25))
  const fromBuffer = BUFFER.splice(0, FLUSH_LIMIT)  // atomic splice
  const batch = [...fromRetry, ...fromBuffer]

  if (batch.length === 0) { isFlushing = false; return }

  try {
    const { error } = await supabase.from('runtime_logs').insert(batch)
    if (error) throw error
    // Success — clear any localStorage fallback
    clearLocalStorageFallback()
  } catch (err) {
    const message = String((err as { message?: unknown })?.message || err)
    if (/runtime_logs|404|not found|does not exist/i.test(message)) {
      remoteLoggingDisabled = true
      clearLocalStorageFallback()
      return
    }

    // FIX PROB-11: Requeue failed batch for retry
    RETRY_QUEUE.push(...batch.slice(0, 100))          // bounded retry queue
    // Tiered fallback: persist to localStorage
    persistToLocalStorage(batch)
  } finally {
    isFlushing = false
  }
}

// ─── LocalStorage fallback ──────────────────────────────────
function persistToLocalStorage(entries: LogEntry[]): void {
  try {
    const existing = JSON.parse(localStorage.getItem(LS_KEY) ?? '[]') as LogEntry[]
    const merged   = [...existing, ...entries].slice(-200)  // keep last 200
    localStorage.setItem(LS_KEY, JSON.stringify(merged))
  } catch { /* localStorage unavailable */ }
}

function clearLocalStorageFallback(): void {
  try { localStorage.removeItem(LS_KEY) } catch { /* ignore */ }
}

export function getLocalStorageLogs(): LogEntry[] {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) ?? '[]') as LogEntry[]
  } catch { return [] }
}

// ─── Observability lifecycle ────────────────────────────────
export function startObservability(): () => void {
  flushTimer = setInterval(() => { void flush() }, FLUSH_INTERVAL)

  // Attempt to replay any persisted logs from previous session
  void replayLocalStorageLogs()

  return () => {
    if (flushTimer) { clearInterval(flushTimer); flushTimer = null }
    void flush()
  }
}

async function replayLocalStorageLogs(): Promise<void> {
  const stored = getLocalStorageLogs()
  if (stored.length === 0) return
  RETRY_QUEUE.push(...stored.slice(0, 50))
  clearLocalStorageFallback()
}

// ─── Public API ───────────────────────────────────────────────
export const obs = {
  debug: (msg: string, ctx?: Record<string, unknown>, obraId?: string) =>
    log('debug', 'system', msg, ctx, obraId),

  info: (category: string, msg: string, ctx?: Record<string, unknown>, obraId?: string) =>
    log('info', category, msg, ctx, obraId),

  warn: (category: string, msg: string, ctx?: Record<string, unknown>, obraId?: string) =>
    log('warn', category, msg, ctx, obraId),

  error: (category: string, msg: string, ctx?: Record<string, unknown>, obraId?: string) =>
    log('error', category, msg, ctx, obraId),

  fatal: (category: string, msg: string, ctx?: Record<string, unknown>, obraId?: string) => {
    log('fatal', category, msg, ctx, obraId)
    // Fatal: force immediate flush
    void flush()
  },

  // FIX PROB-10: pipeline logs now go through buffer (not direct RPC)
  pipeline: (
    obraId:    string,
    pipeline:  string,
    status:    'ok' | 'partial' | 'error' | 'timeout',
    latencyMs?: number,
    error?:    string,
  ) => {
    log('info', 'pipeline', `${pipeline}: ${status}`, {
      pipeline,
      status,
      latency_ms: latencyMs,
      error,
    }, obraId)
    // Also fire RPC best-effort for dedicated pipeline_runs table
    supabase.rpc('log_pipeline_run', {
      p_obra_id:  obraId,
      p_pipeline: pipeline,
      p_status:   status,
      p_latency:  latencyMs ?? null,
      p_error:    error ?? null,
    }).catch(() => { /* already in buffer */ })
  },

  timed: async <T>(
    category: string,
    label:    string,
    fn:       () => Promise<T>,
    obraId?:  string,
  ): Promise<T> => {
    const t0 = Date.now()
    try {
      const result = await fn()
      log('info', category, `${label} completed`, { duration_ms: Date.now() - t0 }, obraId)
      return result
    } catch (err) {
      log('error', category, `${label} failed`, {
        duration_ms: Date.now() - t0,
        error:       String(err),
      }, obraId)
      throw err
    }
  },

  /** Flush immediately — call on app shutdown or critical events */
  flush: () => flush(),

  /** Returns in-memory buffer size — useful for health checks */
  bufferSize: () => BUFFER.length + RETRY_QUEUE.length,
}

function log(
  level:    LogLevel,
  category: string,
  message:  string,
  context?: Record<string, unknown>,
  obraId?:  string,
): void {
  // Console output in non-production
  if (process.env.NODE_ENV !== 'production') {
    const tag = `[${category}]`
    if (level === 'fatal' || level === 'error') console.error(tag, message, context)
    else if (level === 'warn')                  console.warn(tag, message, context)
    else if (level === 'debug')                 console.debug(tag, message)
    else                                        console.info(tag, message)
  }

  // Guard buffer size
  if (BUFFER.length >= MAX_BUFFER) {
    BUFFER.splice(0, FLUSH_LIMIT)  // drop oldest if buffer full
  }

  BUFFER.push({ level, category, message, context, obra_id: obraId })

  // Immediate flush trigger on high-priority
  if (BUFFER.length >= FLUSH_LIMIT || level === 'fatal' || level === 'error') {
    void flush()
  }
}
